import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { CommissioningPackage } from '@completion/models';
import { getCps } from '@completion/selectors';
import { Observable, Subject, of } from 'rxjs';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';

export interface CpSelectDialogData {
  title?: string;
  preselectedCpId?: number;
  excludeCpIds?: number[];
  cps?: CommissioningPackage[]; // optional static list
  cps$?: Observable<CommissioningPackage[]>; // optional observable list
  includePredicate?: (cp: CommissioningPackage) => boolean;
}

@Component({
  selector: 'app-cp-select-dialog',
  templateUrl: './cp-select-dialog.component.html',
  styleUrls: ['./cp-select-dialog.component.scss'],
  standalone: false,
})
export class CpSelectDialogComponent implements OnInit, OnDestroy {
  title = 'Select commissioning package';
  control = new UntypedFormControl('');
  cps: CommissioningPackage[] = [];
  filteredCps$: Observable<CommissioningPackage[]> = of([]);
  selectedCp: CommissioningPackage | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<CpSelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CpSelectDialogData,
  ) {}

  ngOnInit(): void {
    this.title = this.data?.title || this.title;
    const excludeIds = this.data?.excludeCpIds || [];
    const predicate = this.data?.includePredicate;

    const applyFilters = (list: CommissioningPackage[]) =>
      list.filter((cp) => {
        if (excludeIds.includes(cp.id)) return false;
        if (predicate && !predicate(cp)) return false;
        return true;
      });

    if (this.data?.cps && this.data.cps.length) {
      this.cps = applyFilters(this.data.cps);
      this.initFilter();
      this.applyPreselected();
    } else if (this.data?.cps$) {
      this.data.cps$.pipe(takeUntil(this.destroy$)).subscribe((list) => {
        this.cps = applyFilters(list || []);
        this.initFilter();
        this.applyPreselected();
      });
    } else {
      this.store
        .select(getCps)
        .pipe(takeUntil(this.destroy$))
        .subscribe((list) => {
          this.cps = applyFilters(list || []);
          this.initFilter();
          this.applyPreselected();
        });
    }
  }

  private initFilter(): void {
    this.filteredCps$ = this.control.valueChanges.pipe(
      startWith(''),
      map((val) => (typeof val === 'string' ? val : (val && (val as CommissioningPackage).cpNumber) || '')),
      map((term) => this._filter(term)),
    );
  }

  private _filter(term: string): CommissioningPackage[] {
    const v = (term || '').toLowerCase();
    return this.cps.filter((cp) => {
      const sys = cp.system?.name || '';
      const subsys = cp.subSystem?.name || '';
      const num = cp.cpNumber || '';
      return [sys, subsys, num].some((x) => x.toLowerCase().includes(v));
    });
  }

  private applyPreselected(): void {
    if (this.data?.preselectedCpId) {
      const pre = this.cps.find((c) => c.id === this.data!.preselectedCpId);
      if (pre) {
        this.selectedCp = pre;
        this.control.setValue(pre);
      }
    }
  }

  displayCp = (cp: CommissioningPackage | string): string => {
    if (!cp) return '';
    if (typeof cp === 'string') return cp;
    return cp.cpNumber || cp.system?.name || 'CP';
  };

  selectCp(cp: CommissioningPackage): void {
    this.selectedCp = cp;
  }

  save(): void {
    this.dialogRef.close(this.selectedCp);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
